import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import {
  getPetsCompliantSummaryWoLoader,
  updateProcedureByIdWoLoader,
} from "../../../../../redux/reducers/petSlice";
import { AppColors } from "../../../../../util/AppColors";
import { billingTabEnum } from "../../../../../util/enum";
import { getPharmacyStockBatches } from "../pharmacyStockHelpers";

const ADMIN_CATEGORIES = [
  "deworming",
  "diet",
  "flea & tick treatment",
  "fluid therapy",
  "grooming & hygiene care",
  "medication",
  "nutrition and supplements",
  "other parasite treatment",
  "vaccines",
];

const PharmacyAddAdministerDrawer = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = reqDet?.appointment;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinicDet, clinicStocks, services, locations } = useMemo(() => {
    if (!mandateDataRaw) {
      return { clinicDet: {}, clinicStocks: [], services: [], locations: [] };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinicDet: reqData?.clinic ?? {},
        clinicStocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
        services: Array.isArray(reqData?.services) ? reqData.services : [],
        locations: Array.isArray(reqData?.locations) ? reqData.locations : [],
      };
    } catch {
      return { clinicDet: {}, clinicStocks: [], services: [], locations: [] };
    }
  }, [mandateDataRaw]);

  useEffect(() => {
    if (!open) return;
    setSearchQuery("");
    setSelectedItems([]);
    setIsSaving(false);
  }, [open]);

  const getDefaultProcedure = () => reqDet?.procedures?.[0] ?? null;

  const getStockBatches = (stkId) =>
    getPharmacyStockBatches(stkId, clinicStocks, clinicDet?.id, locations);

  const getUOMListByStockId = (stkId) => {
    const stockMat = clinicStocks?.find((stc) => stc?.stockId === stkId);
    return (
      stockMat?.uomMappings
        ?.filter((ml) => ml?.allowSale === "Y" && ml?.show)
        ?.map((ml) => ({
          name: ml?.unit,
          value: ml?.unit,
          conversion: ml?.conversion,
          uomMappingId: ml?.uomMappingId ?? null,
        })) ?? []
    );
  };

  const getAdminList = () =>
    (clinicStocks ?? [])
      ?.filter(
        (dta) =>
          dta?.active === "Y" &&
          ADMIN_CATEGORIES.includes((dta?.category ?? "").toLowerCase()),
      )
      ?.map((li) => {
        const batchList = getStockBatches(li?.stockId);
        const reqBatch = batchList?.[0] ?? null;
        const totalQty = (batchList ?? []).reduce(
          (acc, obj) => acc + Number(obj?.quantity ?? 0),
          0,
        );
        return {
          name: li?.tradeName ?? li?.productName,
          stockId: li?.stockId,
          type: li?.category,
          totalQty,
          batchId: reqBatch?.batchId ?? null,
          unitSellingPrice: reqBatch?.unitSellingPrice ?? 0,
          salesUom: reqBatch?.salesUom ?? null,
        };
      })
      ?.filter((item) => Number(item?.totalQty ?? 0) > 0 && item?.batchId);

  const matchesKeywordSearch = (item, query = "") => {
    const keywords = query
      ?.trim()
      ?.toLowerCase()
      ?.split(/\s+/)
      ?.filter(Boolean);
    if (!keywords?.length) return true;
    const searchableText = [item?.name]
      ?.filter(Boolean)
      ?.join(" ")
      ?.toLowerCase();
    return keywords.every((keyword) => searchableText?.includes(keyword));
  };

  const filteredItems = getAdminList()
    ?.filter((item) => matchesKeywordSearch(item, searchQuery))
    ?.slice(0, 100);

  const isItemSelected = (stockId) =>
    selectedItems.some((item) => item?.stockId === stockId);

  const handleToggleAdd = (item) => {
    if (isItemSelected(item?.stockId)) {
      setSelectedItems((prev) =>
        prev.filter((selected) => selected?.stockId !== item?.stockId),
      );
      return;
    }
    setSelectedItems((prev) => [...prev, { ...item, quantity: 1 }]);
  };

  const onApiCall = () => {
    dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
  };

  const buildAdministerDetail = (item, reqServ, reqVar, reqProced) => {
    const reqStock = clinicStocks?.find(
      (stc) => stc?.stockId === item?.stockId,
    );
    const isNoAmount = reqStock?.autoConsumed || !reqStock?.billable;
    const clinicBatches = getStockBatches(item?.stockId);
    const reqBat =
      clinicBatches?.find((bat) => bat?.batchId === item?.batchId) ??
      clinicBatches?.[0];
    const currLocat = locations?.find((loc) => loc?.id === reqBat?.locationId);
    const reqUnitPrice = isNoAmount
      ? 0
      : Number(reqBat?.unitSellingPrice ?? item?.unitSellingPrice ?? 0);
    const taxPerent =
      reqServ?.taxApplicability?.toLowerCase() === "yes"
        ? Number(reqBat?.cgst ?? 0) + Number(reqBat?.sgst ?? 0)
        : 0;
    const reqPrice = reqUnitPrice * Number(item?.quantity ?? 1);
    const unitPrice = reqPrice / ((100 + taxPerent) / 100 || 1);
    const prdTaxValue = (taxPerent / 100) * unitPrice;
    const uomList = getUOMListByStockId(item?.stockId);
    const defaultUom = uomList?.[0];

    return {
      detailId: null,
      stockId: item?.stockId ?? null,
      batchId: item?.batchId ?? null,
      type: "Administered",
      quantity: Number(item?.quantity ?? 1),
      status: "issued",
      remarks: "",
      itemIssued: true,
      estimatedCost: isNoAmount ? 0 : reqPrice,
      name: item?.name,
      procedureName: item?.name,
      dosage: null,
      route: null,
      isDelete: false,
      serviceId: reqProced?.serviceId ?? null,
      tax: isNoAmount ? 0 : prdTaxValue,
      total: isNoAmount ? 0 : reqPrice,
      mrpPrice: isNoAmount ? 0 : reqUnitPrice,
      procedureVariantId: reqVar?.variantId ?? reqProced?.procedureVariantId,
      patientWorkbook: true,
      mainProcedure: false,
      drugSchedule: reqServ?.drugSchedule ?? null,
      hsnSacCode: reqServ?.hsnSacCode ?? null,
      packageUom: reqBat?.salesUom ?? null,
      expiryDate: reqBat?.expiryDate ?? null,
      batchNo: reqBat?.batchNo ?? null,
      billable: reqStock?.billable,
      autoConsumed: reqStock?.autoConsumed,
      linked: "N",
      billType: reqStock?.autoConsumed
        ? (billingTabEnum?.[currLocat?.category] ?? "pharmacy")
        : (billingTabEnum?.Pharmacy ?? "pharmacy"),
      nextDueDate: null,
      billSeparated: false,
      uomId: defaultUom?.uomMappingId ?? null,
      uomName: defaultUom?.value ?? null,
      billing: true,
    };
  };

  const handleDone = () => {
    if (!selectedItems.length) {
      onClose();
      return;
    }
    const reqProced = getDefaultProcedure();
    if (!reqProced?.procedureId) return;

    const reqServ = services?.find(
      (ser) => ser?.serviceId === reqProced?.serviceId,
    );
    const reqVar = reqServ?.serviceVariants?.find(
      (vnt) => String(vnt?.variantId) === String(reqProced?.procedureVariantId),
    );
    const procedureDetails = selectedItems.map((item) =>
      buildAdministerDetail(item, reqServ, reqVar, reqProced),
    );

    let tax = 0;
    let total = reqVar?.servicePrice ?? reqProced?.serviceCharge ?? 0;
    if (reqServ?.taxApplicability?.toLowerCase() === "yes") {
      tax +=
        (Number(reqVar?.servicePrice ?? 0) / 100) * Number(reqServ?.tax ?? 0);
      total += tax;
    }

    setIsSaving(true);
    dispatch(
      updateProcedureByIdWoLoader({
        id: reqProced.procedureId,
        data: {
          serviceCharge: reqProced?.serviceCharge,
          serviceType: reqProced?.serviceType,
          appointmentId: reqDet?.appointment?.appointmentId,
          procedureDetails,
          tax: Number(Number(tax) * Number(reqProced?.quantity ?? 1)),
          total: Number(Number(total) * Number(reqProced?.quantity ?? 1)),
          mrpPrice: Number(reqVar?.servicePrice ?? 0),
          procedureVariantId: reqVar?.variantId,
          quantity: Number(reqProced?.quantity ?? 1),
          hsnSacCode: reqServ?.hsnSacCode ?? null,
          paymentId: reqDet?.paymentId ?? null,
          doseInSeries: reqProced?.doseInSeries ?? null,
          serviceRemainderId: reqProced?.serviceRemainderId ?? null,
        },
      }),
    ).then((res) => {
      setIsSaving(false);
      if (res?.payload) {
        onApiCall();
        onClose();
      }
    });
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header="Add administered items"
      modal
      modalWidth={40}
      reduceHeaderSize
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{ height: "calc(100vh - 75px)" }}
      >
        <Box px={3} flexGrow={1} overflow="auto">
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: AppColors.appPrimary,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              mb: 1.5,
            }}
          >
            PHARMACY INVENTORY
          </Typography>

          <Box mb={2.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by item name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": { borderRadius: "6px" },
              }}
            />
          </Box>

          <Box mb={3}>
            {filteredItems?.map((item) => (
              <Box
                key={item.stockId}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                py={1.8}
                borderBottom="1px solid #f0f0f0"
              >
                <Box>
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 600, color: "#212121" }}
                  >
                    {item?.name}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#757575" }}>
                    ₹{item?.unitSellingPrice}
                    {item?.salesUom ? ` / ${item.salesUom}` : ""} · Qty:{" "}
                    {item?.totalQty ?? 0}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {isItemSelected(item?.stockId) ? (
                    <Box
                      sx={{
                        backgroundColor: "#e8f5e9",
                        color: "#2e7d32",
                        fontSize: "12px",
                        fontWeight: 600,
                        px: 1.2,
                        py: 0.4,
                        borderRadius: "4px",
                      }}
                    >
                      ✓ Added
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleToggleAdd(item)}
                      sx={{
                        backgroundColor: AppColors.appPrimary,
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "12px",
                        minWidth: "60px",
                        height: "30px",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#003e7e",
                          boxShadow: "none",
                        },
                      }}
                    >
                      + Add
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
            {!filteredItems?.length ? (
              <Typography sx={{ fontSize: "13px", color: "#9e9e9e", py: 2 }}>
                No pharmacy inventory items available.
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          px={3}
          py={2}
          borderTop="1px solid #e0e0e0"
          backgroundColor="#fafafa"
        >
          <CustomButton
            text="Done"
            smallBtn
            disabled={isSaving}
            onClick={handleDone}
          />
        </Box>
      </Box>
    </CustomModal>
  );
};

export default PharmacyAddAdministerDrawer;
